/**
 * Lock-screen / notification artwork for mobile browsers.
 *
 * Without Media Session metadata, Safari falls back to the page favicon — our
 * SVG logo then shows as a tiny mark in the corner of a white square. Prefer
 * the clip poster when we have one; always ship large PNG brand icons as
 * fallbacks so every device has something sized for the widget.
 */

const brandArtwork = (): MediaImage[] => {
  const origin = window.location.origin;
  return [
    { src: `${origin}/icon-192.png`, sizes: "192x192", type: "image/png" },
    { src: `${origin}/icon-512.png`, sizes: "512x512", type: "image/png" }
  ];
};

const artworkFor = (posterUrl: string | null | undefined): MediaImage[] => {
  const brand = brandArtwork();
  if (!posterUrl) return brand;
  return [
    { src: posterUrl, sizes: "512x512", type: "image/jpeg" },
    { src: posterUrl, sizes: "192x192", type: "image/jpeg" },
    ...brand
  ];
};

type MediaSessionControls = {
  play: () => void;
  pause: () => void;
  seekBy: (deltaSec: number) => void;
  seekTo: (timeSec: number) => void;
};

/** Publish Now Playing metadata + wire lock-screen transport controls. */
export const bindClipMediaSession = (
  title: string,
  posterUrl: string | null | undefined,
  controls: MediaSessionControls
): (() => void) => {
  if (!("mediaSession" in navigator)) return () => undefined;

  const session = navigator.mediaSession;
  session.metadata = new MediaMetadata({
    title: title.trim() || "Shared clip",
    artist: "OpenVOD",
    album: "Shared clip",
    artwork: artworkFor(posterUrl)
  });

  const bind = (action: MediaSessionAction, handler: MediaSessionActionHandler | null): void => {
    try {
      session.setActionHandler(action, handler);
    } catch {
      // Older browsers reject unsupported actions; ignore.
    }
  };

  bind("play", () => controls.play());
  bind("pause", () => controls.pause());
  bind("seekbackward", (details) => controls.seekBy(-(details.seekOffset ?? 10)));
  bind("seekforward", (details) => controls.seekBy(details.seekOffset ?? 10));
  bind("seekto", (details) => {
    if (typeof details.seekTime === "number") controls.seekTo(details.seekTime);
  });

  return () => {
    session.metadata = null;
    for (const action of [
      "play",
      "pause",
      "seekbackward",
      "seekforward",
      "seekto"
    ] as MediaSessionAction[]) {
      bind(action, null);
    }
  };
};

export const setClipMediaSessionPlaybackState = (state: MediaSessionPlaybackState): void => {
  if (!("mediaSession" in navigator)) return;
  navigator.mediaSession.playbackState = state;
};

export const setClipMediaSessionPosition = (
  currentTime: number,
  duration: number,
  playbackRate: number
): void => {
  if (!("mediaSession" in navigator) || !("setPositionState" in navigator.mediaSession)) {
    return;
  }
  if (!Number.isFinite(duration) || duration <= 0) return;
  try {
    navigator.mediaSession.setPositionState({
      duration,
      playbackRate: playbackRate > 0 ? playbackRate : 1,
      position: Math.min(Math.max(0, currentTime), duration)
    });
  } catch {
    // Some engines throw if position drifts past duration mid-update.
  }
};
